import { formatCents } from '@/features/catalog/catalog';
import type { AltaEnCola, EstadoEnvio } from '@/core/offline/almacen';

/**
 * Un trabajo cargado en el teléfono que el servidor todavía no confirmó.
 *
 * ## Por qué está en la lista y no en un recuadro aparte
 *
 * Antes vivía arriba de todo, en un panel con título propio, un párrafo
 * explicando el modo sin conexión y su propia lista. Tres problemas, y el
 * tercero es el que importa:
 *
 *  · repetía lo que ya decía la franja de estado y lo que decía el error de la
 *    consulta: tres carteles distintos para el mismo hecho;
 *  · empujaba la lista fuera de la pantalla, que es para lo que se entra;
 *  · **sacaba el trabajo del lugar donde se lo busca**. Alguien que acaba de
 *    cargar un trabajo lo busca entre los del mes, no en un panel de
 *    sincronización. No encontrarlo ahí se lee como que no se guardó.
 *
 * Así que va en la lista, con la misma forma que sus vecinas —cliente y plata
 * enfrentados, campo abajo, fecha y servicios en chico— y una sola diferencia:
 * el renglón de estado, donde las otras dicen «Facturado / Cobrado», dice en
 * qué anda el envío.
 *
 * ## Lo que NO ofrece
 *
 * Ninguna acción sobre la plata. Facturar o cobrar algo que el servidor todavía
 * no vio no significa nada: no hay contra qué guardarlo. Tampoco editar —editar
 * sin señal no está resuelto—. Lo único que se puede hacer, y sólo cuando el
 * servidor lo rechazó, es reintentar.
 */

const ETIQUETA: Record<EstadoEnvio, string> = {
  pendiente: 'Se envía cuando vuelva la señal',
  enviando: 'Enviando…',
  sincronizado: 'Sincronizado',
  error: 'No se pudo sincronizar',
};

const COLOR: Record<EstadoEnvio, string> = {
  pendiente: 'text-[var(--aviso)]',
  enviando: 'text-[var(--tinta-suave)]',
  sincronizado: 'text-[var(--exito)]',
  error: 'text-[var(--peligro)]',
};

export function TarjetaPendiente({
  alta,
  onReintentar,
}: {
  alta: AltaEnCola;
  onReintentar: () => void;
}) {
  const { resumen } = alta;
  const fecha = /^\d{4}-\d{2}-\d{2}$/.test(resumen.fecha)
    ? new Date(`${resumen.fecha}T00:00:00`).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
      })
    : resumen.fecha;

  return (
    <li data-testid="alta-pendiente">
      <div
        className="flex flex-col gap-2 rounded-[var(--radius-superficie)] bg-[var(--superficie)] py-3 pl-3 pr-3"
        // Una barra al costado como la de las demás, pero en el color del
        // aviso: dice de un vistazo que ésta todavía no está del todo.
        style={{ boxShadow: 'inset 3px 0 0 0 var(--aviso)' }}
      >
        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate font-semibold leading-tight">{resumen.cliente}</p>
            <p
              className={`shrink-0 font-semibold tabular-nums ${
                resumen.totalCents == null ? 'text-[var(--aviso)]' : ''
              }`}
              data-testid="pendiente-total"
            >
              {formatCents(resumen.totalCents ?? null)}
            </p>
          </div>

          {resumen.establecimiento && (
            <p className="truncate text-sm text-[var(--tinta-suave)]">{resumen.establecimiento}</p>
          )}

          <p className="truncate text-xs text-[var(--tinta-tenue)]">
            {fecha}
            {resumen.servicios ? ` · ${resumen.servicios}` : ''}
          </p>

          {/* Donde las otras tarjetas dicen «Facturado / Cobrado», ésta dice en
              qué anda. Mismo lugar, mismo tamaño: se recorre la columna sin
              tener que cambiar de forma de leer. */}
          <p
            className={`mt-1.5 text-xs font-medium ${COLOR[alta.estado]}`}
            data-testid={`estado-${alta.estado}`}
          >
            {ETIQUETA[alta.estado]}
          </p>
        </div>

        {alta.estado === 'error' && (
          <div className="flex flex-col gap-1 rounded-[var(--radius-control)] bg-[var(--peligro-fondo)] px-3 py-2">
            {/* El motivo del servidor, tal cual. Traducirlo a «hubo un
                problema» le saca a quien lo lee lo único que le permite
                entender qué corregir. */}
            <span className="text-xs text-[var(--peligro)]">{alta.ultimoError}</span>
            <button
              type="button"
              onClick={onReintentar}
              className="min-h-11 self-start text-sm font-medium text-[var(--peligro)] underline"
              data-testid="reintentar-alta"
            >
              Reintentar
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
